import dynamic from '@/routing/dynamic'
import { useMemo } from 'react'
import {
  aggregateSimple,
  aggregateGroupedWithBases,
  getQuestionValue,
  type ChartDataPoint,
} from '../chart-aggregation'
import { formatLabel } from '@/lib/utils/formatting'
import {
  formatAssetReportLabel,
  type AssetReportContextItem,
} from '../asset-report-label'
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton'
import { TrackBar } from '../TrackBar'
import { CHART_LIMITS } from '../../../foundations/slide-limits'
import { DECK_CHART_PRIMARY_COLOR, DECK_CHART_SERIES_COLORS } from '../../../foundations/chart-colors'
import { LaneMergedSections } from '../LaneMergedSections'
import {
  unfoldResponsesForLane,
  type MonadicLaneScopeFact,
} from '../../../lib/monadicLaneScope'

// Dynamically import Chart.js doughnut chart - only loads when pie view is selected
const PieChartLazy = dynamic(
  () => import('../chartjs/ChartJsPie/ChartJsPie'),
  { 
    loading: () => <ChartSkeleton type="pie" />,
    ssr: false 
  }
)

interface SingleChoiceChartProps {
  question: any
  segmentQuestion?: any
  responses: any[]
  type?: 'bar' | 'pie'
  colors?: string[]
  /** Show declared choices that got 0 responses (e.g. the 0% side of a head-to-head). */
  includeEmptyChoices?: boolean
  assetContextItems?: Array<AssetReportContextItem>
  monadicLaneScope?: MonadicLaneScopeFact | null
  emptyLaneLabel?: string
}

/**
 * @deprecated 2026-10-01: legacy reader only. Do not use in new code; draw the shared
 * plots in `packages/deck-ui/src/plots` instead. Kept so old studies the figure service
 * refuses (and cards whose figure fails) still draw exactly as before.
 * Delete after 2027-01-01, together with the legacy fallback in QuestionCard.
 */
export function SingleChoiceChart(props: SingleChoiceChartProps) {
  const { question, responses, monadicLaneScope, assetContextItems, emptyLaneLabel } = props
  if (unfoldResponsesForLane(question, responses, monadicLaneScope).length > 0) {
    return (
      <LaneMergedSections
        question={question}
        responses={responses}
        monadicLaneScope={monadicLaneScope}
        assetContextItems={assetContextItems}
        testIdPrefix="single-choice-chart"
        emptyLabel={emptyLaneLabel}
      >
        {(unfolded) => (
          <SingleChoiceChartBody {...props} responses={unfolded} segmentQuestion={undefined} monadicLaneScope={null} />
        )}
      </LaneMergedSections>
    )
  }
  return <SingleChoiceChartBody {...props} />
}

function SingleChoiceChartBody({
  question,
  segmentQuestion,
  responses,
  type = 'bar',
  colors: colorsProp,
  includeEmptyChoices = false,
  assetContextItems,
}: SingleChoiceChartProps) {
  const isSegmented = !!segmentQuestion
  const effectiveBarColor = colorsProp?.[0] || DECK_CHART_PRIMARY_COLOR
  const effectiveColors = colorsProp || DECK_CHART_SERIES_COLORS

  const groupedAggregation = useMemo(
    () => isSegmented
      ? aggregateGroupedWithBases(question, segmentQuestion, responses)
      : null,
    [isSegmented, question, segmentQuestion, responses],
  )

  const data = useMemo(() => {
    const aggregated = groupedAggregation?.data
      ?? aggregateSimple(question, responses, includeEmptyChoices)
    return aggregated.map((point): ChartDataPoint => ({
      ...point,
      name: formatAssetReportLabel(
        String(point.name),
        assetContextItems,
        [String(point.rawValue ?? '')],
      ),
    }))
  }, [groupedAggregation, question, responses, includeEmptyChoices, assetContextItems])

  if (!data || data.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
      >
        <svg className="h-8 w-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-sm">No data available</span>
      </div>
    )
  }

  // Segmented View (List Layout)
  if (isSegmented) {
    // Get keys for the Segments (e.g. 18-24, 25-34, etc.)
    const allKeys = new Set<string>()
    data.forEach(point => {
      Object.keys(point).forEach(k => {
        if (k !== 'name' && k !== 'value') {
          allKeys.add(k)
        }
      })
    })
    
    // Each segment base is the number of respondents who answered the target.
    // Checkbox option counts may sum above that base by design.
    const segmentCounts = groupedAggregation?.segmentBases ?? {}
    
    // Sort by count (descending) and limit to configured max
    const allSegmentKeys = Array.from(allKeys)
    const sortedKeys = allSegmentKeys.sort((a, b) => (segmentCounts[b] || 0) - (segmentCounts[a] || 0))
    const stackKeys = sortedKeys.slice(0, CHART_LIMITS.rankingMaxSegments)
    const totalSegments = allSegmentKeys.length

    return (
      /* No max-height here. This branch renders only in the report's survey-data
         tab, which scrolls as a page, and the item / segment caps above already
         bound the block. A clamp cut the tail off with no scrollbar to reveal it. */
      <div className="w-full space-y-8 overflow-hidden pr-4">
        {data.slice(0, CHART_LIMITS.rankingSegmentedItems).map((row) => (
            <div key={row.name} className="space-y-3">
                <div className="text-sm font-semibold text-foreground">
                  {formatLabel(row.name)}
                </div>

                <div className="space-y-3 pl-2">
                    {stackKeys.map((segmentKey, segmentIdx) => {
                        const count = row[segmentKey] || 0
                        // QS-75: normalize by the per-SEGMENT total ("how did this
                        // segment answer") — NOT by the option total (which yields the
                        // population composition, e.g. "60% of pickers are women").
                        const total = segmentCounts[segmentKey] || 1
                        const percentage = Math.round((count / total) * 100)
                        
                        return (
                            <div key={segmentKey} className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-foreground">
                                      {formatLabel(segmentKey)}
                                    </span>
                                    <span className="text-sm font-semibold text-muted-foreground tabular-nums">
                                      {percentage}%
                                    </span>
                                </div>

                                <div className="relative h-[9px]">
                                    <div className="absolute inset-0 bg-foreground/10 rounded-full" />
                                    <div 
                                        className="absolute inset-0 rounded-full transition-all duration-500 ease-out min-w-1"
                                        style={{
                                            width: `${percentage}%`,
                                            backgroundColor: effectiveColors[segmentIdx % effectiveColors.length],
                                        }}
                                        title={`${formatLabel(segmentKey)}: ${percentage}% chose ${formatLabel(row.name)} (${count}/${segmentCounts[segmentKey] || 1})`}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        ))}
        {(data.length > CHART_LIMITS.rankingSegmentedItems || totalSegments > CHART_LIMITS.rankingMaxSegments) && (
             <div className="text-center pt-4">
                <p className="text-sm text-muted-foreground italic">
                    {data.length > CHART_LIMITS.rankingSegmentedItems && `Showing top ${CHART_LIMITS.rankingSegmentedItems} of ${data.length} options.`}
                    {data.length > CHART_LIMITS.rankingSegmentedItems && totalSegments > CHART_LIMITS.rankingMaxSegments && ' '}
                    {totalSegments > CHART_LIMITS.rankingMaxSegments && `Showing top ${CHART_LIMITS.rankingMaxSegments} of ${totalSegments} segments.`}
                </p>
             </div>
        )}
      </div>
    )
  }

  // Non-Segmented View (List Layout matching Likert design)
  if (type !== 'pie') {
      const totalSelections = data.reduce((sum, item) => sum + item.value, 0)
      const isCheckbox = question?.type === 'checkbox' || question?.isMultiselect
      const respondentCount = isCheckbox
        ? responses.filter((response) => {
            const value = getQuestionValue(response, question)
            if (Array.isArray(value)) {
              return value.some(
                (item) => item !== null && item !== undefined && String(item).trim() !== '',
              )
            }
            return value !== null && value !== undefined && String(value).trim() !== ''
          }).length
        : totalSelections
      const singletonCount = data.filter((item) => item.value === 1).length
      const singletonShare = data.length > 0 ? singletonCount / data.length : 0
      const sparseHighCardinality =
        data.length > CHART_LIMITS.horizontalBarMaxItems && singletonShare >= 0.6
      const maxItems = sparseHighCardinality
        ? Math.min(7, CHART_LIMITS.horizontalBarMaxItems)
        : CHART_LIMITS.horizontalBarMaxItems
      const shouldClip = data.length > maxItems
      const headCount = shouldClip ? Math.max(1, maxItems - 1) : data.length
      const clippedData = shouldClip
        ? [
            ...data.slice(0, headCount),
            {
              name: `Other (${data.length - headCount} more)`,
              value: data.slice(headCount).reduce((sum, d) => sum + d.value, 0),
              rawValue: '__other__',
            } as ChartDataPoint,
          ]
        : data
      
      return (
          <div className="w-full space-y-6">
            <div className="space-y-5">
                {clippedData.map((item) => {
                    const widthPercent = respondentCount > 0 ? (item.value / respondentCount) * 100 : 0
                    const displayPercent = respondentCount > 0 ? Math.round((item.value / respondentCount) * 100) : 0
                    return (
                        <div key={item.name} className="space-y-1.5">
                             <div className="flex items-center justify-between gap-4">
                               <div className="text-[14px] font-normal text-foreground">
                                 {formatLabel(item.name)}
                               </div>
                               <div className="shrink-0 text-right flex items-baseline gap-1.5">
                                 <span className="text-sm font-semibold tabular-nums">{item.value}</span>
                                 <span className="tabular-nums text-muted-foreground/70 text-[10px]">{displayPercent}%</span>
                               </div>
                             </div>

                           <TrackBar
                               widthPct={widthPercent}
                               color={effectiveBarColor}
                               value={`${item.value} (${displayPercent}%)`}
                               label={formatLabel(item.name)}
                           />
                        </div>
                    )
                })}
            </div>
            {sparseHighCardinality && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground italic">
                  This field is highly fragmented (many one-off values), so the chart is compressed to top buckets.
                </p>
              </div>
            )}
            {shouldClip && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground italic">
                  Showing top {headCount} of {data.length} options. Remaining {data.length - headCount} grouped as Other.
                </p>
              </div>
            )}
          </div>
      )
  }

  // Pie View - use explicit dimensions to avoid ResponsiveContainer sizing issues in flex layouts

  // Limit to top items and group rest as "Other" for cleaner display
  const maxSlices = CHART_LIMITS.doughnutMaxSegments
  const chartData = data.length > maxSlices
    ? [
        ...data.slice(0, maxSlices - 1),
        {
          name: `Other (${data.length - maxSlices + 1} more)`,
          value: data.slice(maxSlices - 1).reduce((sum, d) => sum + d.value, 0)
        }
      ]
    : data
  
  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full max-w-md h-72">
        <PieChartLazy data={chartData} colors={effectiveColors} />
      </div>
      {/* Custom Legend with truncation for long lists */}
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4 px-4">
        {chartData.map((entry, index) => (
          <div key={entry.name} className="flex items-center gap-1.5 text-sm">
            <span 
              className="w-2.5 h-2.5 rounded-full shrink-0" 
              style={{ backgroundColor: effectiveColors[index % effectiveColors.length] }}
            />
            <span
              className="text-muted-foreground truncate max-w-48"
              title={formatLabel(entry.name)}
            >
              {formatLabel(entry.name)}
            </span>
            <span className="text-muted-foreground/60">({entry.value})</span>
          </div>
        ))}
      </div>
    </div>
  )
}
